import { useEffect, useMemo, useState } from 'react'
import { fetchCounsellorInsights } from 'api/reports'
import { useReportsPeriodControls } from '../../hooks/useReportsPeriodControls'
import { buildInsightsDateParams } from '../../utils/reportsPeriods'
import { downloadCsv } from '../../utils/reportExport'

const totalFor = (rows, key) => rows.reduce((sum, row) => sum + Number(row[key] || 0), 0)

export const formatMinutes = minutes => {
	const total = Math.max(0, Math.floor(Number(minutes) || 0))
	return `${Math.floor(total / 60)}h ${total % 60}m`
}

export const initialsFor = row =>
	row.initials ||
	String(row.name || '')
		.split(' ')
		.map(part => part[0])
		.join('')
		.slice(0, 2)
		.toUpperCase()

const TONES = ['purple', 'blue', 'green', 'orange']

const emptyPayload = () => ({
	period: null,
	me: null,
	team: [],
	summaryMine: null,
	summaryTeam: null,
})

const normalizeRow = row => {
	if (!row) return null
	return {
		id: row.id,
		name: row.name || row.id,
		caseload: Number(row.caseload || 0),
		studentsWithSession: Number(row.studentsWithSession || 0),
		sessions: Number(row.sessions || 0),
		sessionMinutes: Number(row.sessionMinutes || 0),
		tasksCompleted: Number(row.tasksCompleted || 0),
		tasksOpen: Number(row.tasksOpen || 0),
		tasksOverdue: Number(row.tasksOverdue || 0),
		assignmentsGiven: Number(row.assignmentsGiven || 0),
		applications: Number(row.applications || 0),
		tasks: Number(row.tasksCompleted || 0),
		backlog: Number(row.tasksOpen || 0),
		overdueFollowups: Number(row.tasksOverdue || 0),
		coverageLifetime: row.coverageLifetime || {
			withApplication: 0,
			withAssignment: 0,
			withSession: 0,
			withApplicationPct: 0,
			withAssignmentPct: 0,
			withSessionPct: 0,
		},
		coveragePeriod: row.coveragePeriod || {
			withApplication: 0,
			withAssignment: 0,
			withSession: 0,
			withApplicationPct: 0,
			withAssignmentPct: 0,
			withSessionPct: 0,
		},
	}
}

/**
 * Counsellor Report page state. Reporting period from shared MobX applied state.
 */
export const useCounsellorReport = () => {
	const periodControls = useReportsPeriodControls()
	const [view, setView] = useState('mine')
	const [status, setStatus] = useState('loading')
	const [error, setError] = useState(null)
	const [payload, setPayload] = useState(emptyPayload())

	const { appliedPeriod, appliedCustomFrom, appliedCustomTo } = periodControls

	useEffect(() => {
		let active = true
		const dateParams = buildInsightsDateParams({
			period: appliedPeriod,
			customFrom: appliedCustomFrom,
			customTo: appliedCustomTo,
		})

		if (!dateParams) {
			setStatus('idle')
			setError(null)
			return () => {
				active = false
			}
		}

		setStatus('loading')
		setError(null)

		const load = async () => {
			try {
				const data = await fetchCounsellorInsights(dateParams)
				if (!active) return
				setPayload(data || emptyPayload())
				setStatus('success')
				setError(null)
			} catch (err) {
				if (!active) return
				setPayload(emptyPayload())
				setStatus('error')
				setError(
					err?.response?.data?.error ||
						err?.message ||
						'Failed to load counsellor insights',
				)
			}
		}

		load()
		return () => {
			active = false
		}
	}, [appliedPeriod, appliedCustomFrom, appliedCustomTo])

	const me = useMemo(() => normalizeRow(payload.me), [payload.me])
	const teamRows = useMemo(
		() => (payload.team || []).map(normalizeRow).filter(Boolean),
		[payload.team],
	)

	const rows = useMemo(() => {
		if (view === 'mine') {
			return me ? [me] : []
		}
		return [...teamRows].sort((a, b) => {
			const covA = a.caseload ? Number(a.studentsWithSession || 0) / a.caseload : 0
			const covB = b.caseload ? Number(b.studentsWithSession || 0) / b.caseload : 0
			return covB - covA
		})
	}, [view, me, teamRows])

	const isMine = view === 'mine'

	const summary = useMemo(
		() => ({
			caseload: totalFor(rows, 'caseload'),
			studentsWithSession: totalFor(rows, 'studentsWithSession'),
			sessions: totalFor(rows, 'sessions'),
			minutes: totalFor(rows, 'sessionMinutes'),
			tasks: totalFor(rows, 'tasksCompleted'),
			assignmentsGiven: totalFor(rows, 'assignmentsGiven'),
			applications: totalFor(rows, 'applications'),
			open: totalFor(rows, 'tasksOpen'),
			overdue: totalFor(rows, 'tasksOverdue'),
		}),
		[rows],
	)

	const coverage = summary.caseload
		? Math.min(100, Math.round((summary.studentsWithSession / summary.caseload) * 100))
		: 0

	const activityRows = [
		{ id: 'sessions', label: 'Completed sessions', value: summary.sessions, tone: 'purple' },
		{ id: 'tasks', label: 'Tasks completed', value: summary.tasks, tone: 'blue' },
		{
			id: 'assignments',
			label: 'Assignments given',
			value: summary.assignmentsGiven,
			tone: 'orange',
		},
	]

	const exportRows = rows.map(row => ({
		counsellor: row.name,
		assigned_students: row.caseload,
		students_with_completed_session: row.studentsWithSession,
		completed_sessions: row.sessions,
		recorded_session_minutes: row.sessionMinutes,
		completed_tasks: row.tasksCompleted,
		assignments_given: row.assignmentsGiven,
		applications: row.applications,
		open_followups: row.tasksOpen,
		overdue_followups: row.tasksOverdue,
	}))

	const handleExport = () => downloadCsv('counsellor-report.csv', exportRows)

	return {
		...periodControls,
		period: periodControls.selectedPeriod,
		customFrom: periodControls.draftFrom,
		customTo: periodControls.draftTo,
		setPeriod: periodControls.onPeriodChange,
		setCustomFrom: periodControls.onCustomFromChange,
		setCustomTo: periodControls.onCustomToChange,
		status,
		error,
		view,
		setView,
		isMine,
		rows,
		me,
		summary,
		coverage,
		activityRows,
		handleExport,
		tones: TONES,
	}
}
